import { getEffectiveLottieParameterValues } from './contract';
import { getAuthoredLottieText } from './materialize';
import { loadLottieFonts } from './useLoadLottieFonts';
import { __, sprintf } from '@web-stories-wp/i18n';

const CONFIG_KEY = 'storytellerTextRows';
const DRAFT_KEY = 'storytellerTextRowsDraft';

const readPointer = (value, pointer) =>
  pointer
    .slice(1)
    .split('/')
    .reduce(
      (current, key) => current?.[key.replace(/~1/g, '/').replace(/~0/g, '~')],
      value
    );

export function getLottieTextRowsConfig(element) {
  const config = element?.definition?.meta?.[CONFIG_KEY];
  if (
    element?.type !== 'lottie' ||
    config?.version !== 2 ||
    !Array.isArray(config.rows) ||
    !config.rows.length
  ) {
    return null;
  }

  const parameter = element.parameterSchema?.parameters?.find(
    ({ key, type }) => key === config.parameterKey && type === 'text'
  );
  if (!parameter) {
    return null;
  }
  const rows = config.rows.map((row) => {
    const { textDocumentPointer, maxWidth } = row || {};
    const style =
      typeof textDocumentPointer === 'string' &&
      textDocumentPointer.startsWith('/')
        ? readPointer(element.definition, textDocumentPointer)
        : null;
    const font = element.definition.fonts?.list?.find(
      ({ fName }) => fName === style?.f
    );

    if (
      !font?.fFamily ||
      !Number.isFinite(style?.s) ||
      style.s <= 0 ||
      !Number.isFinite(maxWidth) ||
      maxWidth <= 0
    ) {
      return null;
    }

    return {
      maxWidth,
      fontFamily: font.fFamily,
      fontSize: style.s,
      tracking: Number.isFinite(style.tr) ? style.tr : 0,
      fontStyle: /italic/i.test(font.fStyle) ? 'italic' : 'normal',
      fontWeight: /bold/i.test(font.fStyle) ? 700 : 400,
    };
  });

  if (
    rows.some((row) => !row) ||
    new Set(config.rows.map(({ textDocumentPointer }) => textDocumentPointer))
      .size !== rows.length
  ) {
    return null;
  }

  return {
    parameterKey: parameter.key,
    label: parameter.label || 'Copy',
    maxLength: parameter.constraints?.maxLength,
    rows,
  };
}

export const getLottieTextRowsValue = (
  element,
  config = getLottieTextRowsConfig(element)
) => {
  const values = getEffectiveLottieParameterValues(element);
  if (Object.prototype.hasOwnProperty.call(values, config.parameterKey)) {
    return String(values[config.parameterKey] ?? '');
  }
  const parameter = element.parameterSchema.parameters.find(
    ({ key }) => key === config.parameterKey
  );
  return getAuthoredLottieText(element.definition, parameter);
};

// Font readiness changes derived validation/preview state, not authored Copy.
// Merge only that resolution into its existing history entry; other edits
// (including geometry, fonts and ordinary parameter resets) stay undoable.
export function isLottieTextRowsResolution(previousPages, nextPages) {
  let resolved = false;
  const normalized = nextPages.map((page, pageIndex) => ({
    ...page,
    elements: page.elements.map((element, elementIndex) => {
      const previous = previousPages[pageIndex]?.elements[elementIndex];
      const config = getLottieTextRowsConfig(previous);
      const nextConfig = getLottieTextRowsConfig(element);
      if (
        !config ||
        previous.id !== element.id ||
        !nextConfig ||
        (!getDraft(previous, config) && !getDraft(element, nextConfig)) ||
        getLottieTextRowsSource(previous) !== getLottieTextRowsSource(element)
      ) {
        return element;
      }
      const comparable = {
        ...element,
        parameterValues: previous.parameterValues,
        definition: {
          ...element.definition,
          meta: {
            ...element.definition.meta,
            [DRAFT_KEY]: previous.definition.meta[DRAFT_KEY],
          },
        },
      };
      // Do not merge changes to unrelated parameters into a font resolution.
      const parameters = { ...element.parameterValues };
      const previousParameters = { ...previous.parameterValues };
      delete parameters[config.parameterKey];
      delete previousParameters[config.parameterKey];
      if (JSON.stringify(parameters) !== JSON.stringify(previousParameters)) {
        return element;
      }
      resolved = true;
      return comparable;
    }),
  }));
  return (
    resolved && JSON.stringify(previousPages) === JSON.stringify(normalized)
  );
}

const getDraft = (element, config) => {
  const draft = element.definition.meta?.[DRAFT_KEY];
  return typeof draft?.copy === 'string' &&
    draft.value === getLottieTextRowsValue(element, config)
    ? draft
    : null;
};

export function getLottieTextRowsSource(element) {
  const config = getLottieTextRowsConfig(element);
  if (!config) {
    return '';
  }
  const draft = getDraft(element, config);
  if (draft) {
    return draft.copy;
  }
  return getLottieTextRowsValue(element, config);
}

export function getLottieTextRowsMessage(layout) {
  if (layout.status === 'pending') {
    return __(
      'Copy is still being checked. Wait for the template font to load and try again.',
      'web-stories'
    );
  }
  if (layout.status === 'row-limit') {
    return sprintf(
      __(
        'Copy exceeds %d rows. Remove a line break before generating.',
        'web-stories'
      ),
      layout.maxRows
    );
  }
  if (layout.status === 'line-width') {
    return sprintf(
      __(
        'Line %d is too wide. Shorten it or add a line break before generating.',
        'web-stories'
      ),
      layout.rowNumber
    );
  }
  if (layout.status === 'copy-length') {
    return __(
      'Copy exceeds its character limit. Shorten it before generating.',
      'web-stories'
    );
  }
  return __(
    'The template font could not be loaded or measured. Check its font mapping and try again.',
    'web-stories'
  );
}

export function getLottieTextRowsIssue(element) {
  const config = getLottieTextRowsConfig(element);
  if (!config) {
    return null;
  }
  const draft = getDraft(element, config);
  if (!draft || draft.status === 'ready') {
    return null;
  }
  return getLottieTextRowsMessage({ ...draft, maxRows: config.rows.length });
}

// Recheck even unselected/default copy at the final generation boundary.
export async function validateLottieTextRowsElement(element) {
  const config = getLottieTextRowsConfig(element);
  if (!config) {
    return null;
  }
  const draftIssue = getLottieTextRowsIssue(element);
  if (draftIssue) {
    return draftIssue;
  }
  let measurer;
  try {
    measurer = await loadLottieTextRowsMeasurer(config.rows, element.fonts);
    const layout = validateLottieTextRows(
      getLottieTextRowsSource(element),
      config.rows,
      measurer.measure,
      config.maxLength
    );
    return layout.status === 'ready' ? null : getLottieTextRowsMessage(layout);
  } catch {
    return getLottieTextRowsMessage({ status: 'font-error' });
  } finally {
    measurer?.destroy();
  }
}

export function createLottieTextRowsUpdate(element, copy, layout) {
  const config = getLottieTextRowsConfig(element);
  if (!config) {
    return {};
  }
  const parameterValues = { ...element.parameterValues };
  const meta = { ...element.definition.meta };
  if (layout.status === 'ready') {
    // An empty override is intentional: removing it restores the CMS default.
    parameterValues[config.parameterKey] = copy;
    delete meta[DRAFT_KEY];
  } else {
    meta[DRAFT_KEY] = {
      copy,
      value: getLottieTextRowsValue(element, config),
      status: layout.status,
      ...(layout.rowNumber ? { rowNumber: layout.rowNumber } : {}),
    };
  }

  return {
    parameterValues,
    definition: {
      ...element.definition,
      meta,
    },
  };
}

export function resetLottieTextRows(element) {
  const config = getLottieTextRowsConfig(element);
  const parameterValues = { ...element.parameterValues };
  delete parameterValues[config.parameterKey];
  const meta = { ...element.definition.meta };
  delete meta[DRAFT_KEY];
  return { parameterValues, definition: { ...element.definition, meta } };
}

const segmenter =
  typeof Intl.Segmenter === 'function'
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    : null;
const splitCharacters = (text) =>
  segmenter
    ? Array.from(segmenter.segment(text), ({ segment }) => segment)
    : Array.from(text);

// Only explicit line breaks create rows; never wrap, shrink or truncate copy.
export function validateLottieTextRows(copy, rows, measure, maxLength) {
  const lines = copy.replace(/\r\n?/g, '\n').split('\n');
  if (lines.length > rows.length) {
    return { status: 'row-limit', maxRows: rows.length };
  }
  if (Number.isFinite(maxLength) && copy.length > maxLength) {
    return { status: 'copy-length' };
  }
  for (const [index, line] of lines.entries()) {
    const row = rows[index];
    if (measure(line, index) > row.maxWidth) {
      return { status: 'line-width', rowNumber: index + 1 };
    }
  }
  return {
    status: 'ready',
    lines: rows.map((_, index) => lines[index] ?? ''),
  };
}

export async function loadLottieTextRowsMeasurer(rows, fonts = []) {
  if (
    rows.some((row) => !fonts.some((font) => font.family === row.fontFamily))
  ) {
    throw new Error('The template font mapping is missing.');
  }
  await loadLottieFonts(fonts);
  const measurer = createLottieTextMeasurer(rows);
  try {
    rows.forEach((_, index) => measurer.measure('M', index));
    return measurer;
  } catch (error) {
    measurer.destroy();
    throw error;
  }
}

export function createLottieTextMeasurer(rows) {
  const namespace = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(namespace, 'svg');
  svg.setAttribute('aria-hidden', 'true');
  svg.style.cssText =
    'position:fixed;left:-10000px;top:0;width:2000px;height:2000px;opacity:0;pointer-events:none;overflow:visible';
  const texts = rows.map((row) => {
    const text = document.createElementNS(namespace, 'text');
    text.setAttribute('font-family', row.fontFamily);
    text.setAttribute('font-size', '100');
    text.setAttribute('font-style', row.fontStyle);
    text.setAttribute('font-weight', String(row.fontWeight));
    text.style.whiteSpace = 'pre';
    text.setAttributeNS(
      'http://www.w3.org/XML/1998/namespace',
      'xml:space',
      'preserve'
    );
    svg.appendChild(text);
    return text;
  });
  document.body.appendChild(svg);
  const caches = rows.map(() => new Map());

  return {
    measure(value, rowIndex) {
      const row = rows[rowIndex];
      const scale = row.fontSize / 100;
      let cursor = 0;
      let left = Infinity;
      let right = -Infinity;
      for (const character of splitCharacters(value)) {
        let glyph = caches[rowIndex].get(character);
        if (!glyph) {
          const text = texts[rowIndex];
          text.textContent = character;
          const bounds = text.getBBox();
          glyph = {
            x: bounds.x,
            width: bounds.width,
            advance: text.getComputedTextLength(),
          };
          if (!glyph.advance && character.trim()) {
            throw new Error('The template font could not be measured.');
          }
          caches[rowIndex].set(character, glyph);
        }
        left = Math.min(left, cursor + glyph.x * scale);
        right = Math.max(right, cursor + (glyph.x + glyph.width) * scale);
        cursor += glyph.advance * scale + (row.tracking * row.fontSize) / 1000;
      }
      return value ? right - left : 0;
    },
    destroy() {
      svg.remove();
    },
  };
}
